/**
 * Tooltip Component
 * Hybrid approach: Hover for desktop, Long-press for mobile
 */
import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTouchDevice } from '../../hooks/useTouchDevice';

interface TooltipProps {
  content: string;
  children: ReactNode;
  position?: 'top' | 'bottom';
  disabled?: boolean;
}

export default function Tooltip({ content, children, position = 'top', disabled = false }: TooltipProps) {
  const { user } = useAuthStore();
  const isTouch = useTouchDevice();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);

  // Check if tooltips are enabled in user settings (default: true)
  const tooltipsEnabled = user?.show_tooltips !== false;

  // Don't render tooltip if disabled or not enabled in settings
  if (disabled || !tooltipsEnabled) {
    return <>{children}</>;
  }

  // Desktop: Mouse hover handlers
  const handleMouseEnter = () => {
    if (!isTouch) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouch) {
      setShowTooltip(false);
    }
  };

  // Mobile: Touch/long-press handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isTouch) {
      // Don't interfere with scrolling
      e.stopPropagation();

      pressTimer.current = setTimeout(() => {
        setShowTooltip(true);

        // Auto-hide after 2.5 seconds
        hideTimer.current = setTimeout(() => {
          setShowTooltip(false);
        }, 2500);
      }, 500); // Show after 500ms long-press
    }
  };

  const handleTouchEnd = () => {
    if (isTouch && pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleTouchCancel = () => {
    if (isTouch) {
      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
        pressTimer.current = null;
      }
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      setShowTooltip(false);
    }
  };

  // Calculate tooltip position when shown
  useEffect(() => {
    if (showTooltip && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const calculatedPosition = {
        top: position === 'top' ? rect.top - 8 : rect.bottom + 8,
        left: rect.left + rect.width / 2,
      };
      setTooltipPosition(calculatedPosition);
    } else {
      setTooltipPosition(null);
    }
  }, [showTooltip, position, content]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      >
        {children}
      </div>

      {showTooltip && tooltipPosition && createPortal(
        <div
          className={`
            fixed z-[9999] px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg
            whitespace-nowrap pointer-events-none
            ${position === 'top' ? '-translate-y-full' : 'translate-y-0'}
            -translate-x-1/2
          `}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxWidth: '250px',
            whiteSpace: 'normal',
            border: '2px solid white',
          }}
        >
          {content}

          {/* Arrow */}
          <div
            className={`
              absolute left-1/2 -translate-x-1/2 w-0 h-0
              border-l-[6px] border-l-transparent
              border-r-[6px] border-r-transparent
              ${position === 'top'
                ? 'bottom-0 translate-y-full border-t-[6px] border-t-gray-900'
                : 'top-0 -translate-y-full border-b-[6px] border-b-gray-900'
              }
            `}
          />
        </div>,
        document.body
      )}
    </>
  );
}
